// ════════════════════════════════════════════════════════════════════════════
// ComprasTab — compras de la tienda (con su estado de entrega) y movimientos del
// ledger de cristales. Las cuentas admin quedan fuera por defecto.
// ════════════════════════════════════════════════════════════════════════════
import React, { useEffect, useMemo, useState } from 'react';
import { AlertCircle, CheckCircle, ChevronDown, ChevronRight, Search, X } from 'lucide-react';
import axios from 'axios';
import { BACKEND_URL, apiErr, DELIVERY, REASON_LABELS } from '../shared';
import { dayKey, fmtDateTime, fmtNum, parseDate } from '../../../lib/fmt';
import DataTable from '../../../components/admin/DataTable';

const norm = (s) => String(s ?? '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

function SearchBox({ value, onChange, placeholder, testId }) {
  return (
    <div style={{ position: 'relative' }}>
      <Search size={14} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'rgba(0,229,255,0.5)', pointerEvents: 'none' }} />
      <input className="adm-input" style={{ paddingLeft: 30, paddingRight: 26, width: 'min(260px, 70vw)' }}
        placeholder={placeholder} value={value} onChange={(e) => onChange(e.target.value)} data-testid={testId} />
      {value && (
        <button type="button" onClick={() => onChange('')} aria-label="Borrar búsqueda"
          style={{ position: 'absolute', right: 6, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 0, color: 'rgba(224,247,255,0.5)', cursor: 'pointer', display: 'flex' }}>
          <X size={14} />
        </button>
      )}
    </div>
  );
}

// ── Compras ──────────────────────────────────────────────────────────────────
function PurchasesView({ onViewStats }) {
  const [purchases, setPurchases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [q, setQ] = useState('');
  const [product, setProduct] = useState('');
  const [status, setStatus] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [onlyVoucher, setOnlyVoucher] = useState(false);
  const [withAdmins, setWithAdmins] = useState(false);
  const [openId, setOpenId] = useState(null);

  useEffect(() => {
    setLoading(true);
    axios.get(`${BACKEND_URL}/api/admin/purchases`, { withCredentials: true })
      .then(({ data }) => { setPurchases(data.purchases || []); setLoadError(null); })
      .catch((err) => { setPurchases([]); setLoadError(apiErr(err, 'No se pudieron cargar las compras.')); })
      .finally(() => setLoading(false));
  }, []);

  const products = useMemo(() => {
    const m = new Map();
    purchases.forEach((p) => m.set(p.product_id || p.product_name, p.product_name || p.product_id));
    return [...m.entries()].sort((a, b) => String(a[1]).localeCompare(String(b[1]), 'es'));
  }, [purchases]);

  const term = norm(q.trim());
  const filtered = useMemo(() => purchases.filter((p) => {
    if (!withAdmins && p.is_admin) return false;
    if (product && (p.product_id || p.product_name) !== product) return false;
    if (status && p.delivery?.status !== status) return false;
    if (onlyVoucher && !p.voucher_used) return false;
    const day = dayKey(p.purchased_at);
    if (from && (!day || day < from)) return false;
    if (to && (!day || day > to)) return false;
    if (term && ![p.username, p.character_name, p.product_name].some((v) => norm(v).includes(term))) return false;
    return true;
  }), [purchases, withAdmins, product, status, onlyVoucher, from, to, term]);

  const totalCrystals = filtered.reduce((s, p) => s + (p.voucher_used ? 0 : (p.price_crystals || 0)), 0);
  const withVoucher = filtered.filter((p) => p.voucher_used).length;
  const anyFilter = q || product || status || from || to || onlyVoucher || withAdmins;
  const clear = () => { setQ(''); setProduct(''); setStatus(''); setFrom(''); setTo(''); setOnlyVoucher(false); setWithAdmins(false); };

  const columns = [
    { key: 'purchased_at', label: 'Fecha', width: 150, sortValue: (p) => parseDate(p.purchased_at)?.getTime(),
      csv: (p) => fmtDateTime(p.purchased_at, ''), render: (p) => <span className="adm-mut">{fmtDateTime(p.purchased_at)}</span> },
    { key: 'username', label: 'Usuario', width: 150, hideable: false,
      render: (p) => (
        <button type="button" className="adm-link" style={{ color: p.is_admin ? '#FFD700' : '#00e5ff' }}
          onClick={() => onViewStats && onViewStats(p.username)} title="Abrir ficha de la cuenta">{p.username}</button>
      ) },
    { key: 'product_name', label: 'Producto', width: 230, title: (p) => p.product_name,
      render: (p) => (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, maxWidth: '100%' }}>
          {p.product_icon && (
            <img src={`https://wow.zamimg.com/images/wow/icons/small/${p.product_icon}.jpg`} alt="" width={18} height={18}
              style={{ borderRadius: 4, flexShrink: 0 }} onError={(e) => { e.target.style.display = 'none'; }} />
          )}
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{p.product_name}</span>
        </span>
      ) },
    { key: 'character_name', label: 'Personaje', width: 130, render: (p) => p.character_name || <span className="adm-mut">—</span> },
    { key: 'price_crystals', label: 'Precio', width: 100, align: 'right', sortValue: (p) => (p.voucher_used ? -1 : p.price_crystals || 0),
      csv: (p) => (p.voucher_used ? 'bono' : p.price_crystals),
      render: (p) => (p.voucher_used
        ? <b style={{ color: '#fcd34d' }}>bono</b>
        : <><b style={{ color: '#00e5ff' }}>{fmtNum(p.price_crystals ?? 0)}</b> <span className="adm-mut">❄</span></>) },
    { key: 'delivery', label: 'Entrega', width: 150, sortValue: (p) => p.delivery?.status,
      csv: (p) => (DELIVERY[p.delivery?.status] || DELIVERY.unknown).label,
      render: (p) => {
        const d = DELIVERY[p.delivery?.status] || DELIVERY.unknown;
        return (
          <button type="button" onClick={() => setOpenId(openId === p.id ? null : p.id)} title={d.hint || 'Ver el detalle de la entrega'}
            style={{ background: 'none', border: 0, padding: 0, cursor: 'pointer', color: d.color, fontWeight: 700, fontSize: 12, display: 'inline-flex', alignItems: 'center', gap: 3 }}>
            {d.label} {openId === p.id ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
          </button>
        );
      } },
    { key: 'action_result', label: 'Resultado', width: 260, defaultHidden: true, title: (p) => p.action_result,
      render: (p) => <span className="adm-mono adm-mut">{p.action_result || '—'}</span> },
  ];

  if (loading) return <div className="armory-empty">Cargando compras...</div>;
  if (loadError) return <div className="register-error"><AlertCircle size={15} /><span>{loadError}</span></div>;

  return (
    <>
      <p className="adm-mut" style={{ fontSize: 12.5, margin: '0 0 10px' }} data-testid="purchases-totals">
        <b style={{ color: '#e0f7ff' }}>{fmtNum(filtered.length)}</b> {filtered.length === 1 ? 'compra' : 'compras'}
        {' · '}<b style={{ color: '#00e5ff' }}>{fmtNum(totalCrystals)} ❄</b> cobrados
        {withVoucher > 0 && <> · {withVoucher} con bono</>}
        {!withAdmins && <> · sin cuentas admin</>}
      </p>
      <DataTable
        testId="purchases-table"
        storageKey="fb-admin-purchases-table-v1"
        columns={columns}
        rows={filtered}
        rowKey={(p) => p.id}
        defaultSort={{ key: 'purchased_at', dir: 'desc' }}
        expandedKey={openId}
        renderExpanded={(p) => {
          const d = DELIVERY[p.delivery?.status] || DELIVERY.unknown;
          return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12 }}>
              {d.hint && <div style={{ color: d.color }}>{d.hint}</div>}
              {p.delivery?.detail && <div><span className="adm-mut">Worldserver:</span> {p.delivery.detail}</div>}
              <div><span className="adm-mut">Resultado:</span> <span className="adm-mono" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{p.action_result || '—'}</span></div>
            </div>
          );
        }}
        csvName="compras"
        emptyText={anyFilter ? 'Ninguna compra coincide con los filtros.' : 'Aún no se ha registrado ninguna compra.'}
        toolbar={(
          <>
            <SearchBox value={q} onChange={setQ} placeholder="Cuenta, personaje o producto…" testId="purchases-search" />
            <select className="adm-select" value={product} onChange={(e) => setProduct(e.target.value)} aria-label="Producto" style={{ maxWidth: 220 }}>
              <option value="">Todos los productos</option>
              {products.map(([id, name]) => <option key={id} value={id}>{name}</option>)}
            </select>
            <select className="adm-select" value={status} onChange={(e) => setStatus(e.target.value)} aria-label="Entrega" data-testid="purchases-status">
              <option value="">Cualquier entrega</option>
              {Object.entries(DELIVERY).map(([k, d]) => <option key={k} value={k}>{d.label}</option>)}
            </select>
            <label className="adm-check">Desde <input type="date" className="adm-input" style={{ colorScheme: 'dark' }} value={from} onChange={(e) => setFrom(e.target.value)} /></label>
            <label className="adm-check">Hasta <input type="date" className="adm-input" style={{ colorScheme: 'dark' }} value={to} onChange={(e) => setTo(e.target.value)} /></label>
            <label className="adm-check"><input type="checkbox" checked={onlyVoucher} onChange={(e) => setOnlyVoucher(e.target.checked)} /> Solo con bono</label>
            <label className="adm-check"><input type="checkbox" checked={withAdmins} onChange={(e) => setWithAdmins(e.target.checked)} data-testid="purchases-with-admins" /> Incluir admins</label>
            {anyFilter && <button type="button" className="adm-btn adm-btn--ghost" onClick={clear}><X size={13} /> Limpiar</button>}
          </>
        )}
      />
    </>
  );
}

// ── Movimientos de cristales (ledger) ────────────────────────────────────────
function LedgerView({ onViewStats }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [reconcile, setReconcile] = useState(null);
  const [q, setQ] = useState('');
  const [reason, setReason] = useState('');
  const [withAdmins, setWithAdmins] = useState(false);

  useEffect(() => {
    setLoading(true);
    Promise.allSettled([
      axios.get(`${BACKEND_URL}/api/admin/crystal-ledger`, { params: { limit: 2000 }, withCredentials: true }),
      axios.get(`${BACKEND_URL}/api/admin/crystal-ledger/reconcile`, { withCredentials: true }),
    ]).then(([l, r]) => {
      if (l.status === 'fulfilled') { setRows((l.value.data.movements || []).map((m, i) => ({ ...m, _k: i }))); setLoadError(null); }
      else setLoadError(apiErr(l.reason, 'No se pudo cargar el ledger.'));
      if (r.status === 'fulfilled') setReconcile(r.value.data);
    }).finally(() => setLoading(false));
  }, []);

  const reasons = useMemo(() => [...new Set(rows.map((r) => r.reason))].sort(), [rows]);
  const term = norm(q.trim());
  const filtered = useMemo(() => rows.filter((r) => {
    if (!withAdmins && r.is_admin) return false;
    if (reason && r.reason !== reason) return false;
    if (term && ![r.username, r.actor, r.ref].some((v) => norm(v).includes(term))) return false;
    return true;
  }), [rows, withAdmins, reason, term]);
  const net = filtered.reduce((s, r) => s + (r.pre_opening ? 0 : r.delta || 0), 0);

  const columns = [
    { key: 'created_at', label: 'Fecha', width: 150, sortValue: (r) => parseDate(r.created_at)?.getTime(),
      csv: (r) => fmtDateTime(r.created_at, ''), render: (r) => <span className="adm-mut">{fmtDateTime(r.created_at)}</span> },
    { key: 'username', label: 'Usuario', width: 150, hideable: false,
      render: (r) => (r.username
        ? <button type="button" className="adm-link" style={{ color: r.is_admin ? '#FFD700' : '#00e5ff' }} onClick={() => onViewStats && onViewStats(r.username)}>{r.username}</button>
        : <span className="adm-mut">—</span>) },
    { key: 'reason', label: 'Motivo', width: 190, csv: (r) => REASON_LABELS[r.reason] || r.reason,
      render: (r) => <>{REASON_LABELS[r.reason] || r.reason}{r.pre_opening && <span className="adm-chip" style={{ marginLeft: 6 }} title="Anterior a la apertura: no cuenta en el cuadre">histórico</span>}</> },
    { key: 'delta', label: 'Movimiento', width: 120, align: 'right',
      render: (r) => <b style={{ color: r.delta >= 0 ? '#a78bfa' : '#f87171' }}>{r.delta > 0 ? '+' : r.delta < 0 ? '−' : ''}{fmtNum(Math.abs(r.delta || 0))} ❄</b> },
    { key: 'balance_after', label: 'Saldo después', width: 120, align: 'right',
      render: (r) => (r.balance_after != null ? fmtNum(r.balance_after) : <span className="adm-mut">—</span>) },
    { key: 'actor', label: 'Hecho por', width: 120, render: (r) => r.actor || <span className="adm-mut">—</span> },
    { key: 'ref', label: 'Referencia', width: 180, title: (r) => r.ref, render: (r) => <span className="adm-mono adm-mut">{r.ref || '—'}</span> },
  ];

  if (loading) return <div className="armory-empty">Cargando movimientos...</div>;
  if (loadError) return <div className="register-error"><AlertCircle size={15} /><span>{loadError}</span></div>;

  return (
    <>
      {reconcile && (
        <div className={`admin-feedback ${reconcile.mismatches ? 'admin-feedback--err' : 'admin-feedback--ok'}`} style={{ margin: '0 0 12px' }} data-testid="ledger-reconcile">
          {reconcile.mismatches ? <AlertCircle size={15} /> : <CheckCircle size={15} />}
          <span>
            {reconcile.mismatches
              ? `${reconcile.mismatches} de ${fmtNum(reconcile.users)} cuentas no cuadran (saldo ≠ suma del ledger): ${reconcile.detail.slice(0, 5).map((d) => `${d.username} ${fmtNum(d.balance)} vs ${fmtNum(d.ledger)}`).join(' · ')}`
              : `Ledger cuadrado: el saldo de las ${fmtNum(reconcile.users)} cuentas coincide con sus movimientos.`}
          </span>
        </div>
      )}
      <p className="adm-mut" style={{ fontSize: 12.5, margin: '0 0 10px' }}>
        <b style={{ color: '#e0f7ff' }}>{fmtNum(filtered.length)}</b> movimientos · neto <b style={{ color: net >= 0 ? '#a78bfa' : '#f87171' }}>{net > 0 ? '+' : net < 0 ? '−' : ''}{fmtNum(Math.abs(net))} ❄</b>
        {rows.length >= 2000 && <> · se muestran los 2.000 más recientes</>} · registro desde el 09/10/2026
      </p>
      <DataTable
        testId="ledger-table"
        storageKey="fb-admin-ledger-table-v1"
        columns={columns}
        rows={filtered}
        rowKey={(r) => r._k}
        defaultSort={{ key: 'created_at', dir: 'desc' }}
        csvName="ledger-cristales"
        emptyText="Ningún movimiento coincide con los filtros."
        toolbar={(
          <>
            <SearchBox value={q} onChange={setQ} placeholder="Cuenta, admin o referencia…" />
            <select className="adm-select" value={reason} onChange={(e) => setReason(e.target.value)} aria-label="Motivo">
              <option value="">Todos los motivos</option>
              {reasons.map((r) => <option key={r} value={r}>{REASON_LABELS[r] || r}</option>)}
            </select>
            <label className="adm-check"><input type="checkbox" checked={withAdmins} onChange={(e) => setWithAdmins(e.target.checked)} /> Incluir admins</label>
          </>
        )}
      />
    </>
  );
}

export default function ComprasTab({ onViewStats }) {
  const [view, setView] = useState('compras');
  return (
    <div className="admin-section anim-fade-up" style={{ animationDelay: '0.15s' }}>
      <div className="admin-section-header" style={{ flexWrap: 'wrap', gap: 10 }}>
        <h2 className="admin-section-title">{view === 'compras' ? 'Compras de la tienda' : 'Movimientos de cristales'}</h2>
        <div style={{ display: 'flex', gap: 6 }}>
          <button type="button" className={`adm-btn${view === 'compras' ? ' adm-btn--active' : ''}`} onClick={() => setView('compras')}>Compras</button>
          <button type="button" className={`adm-btn${view === 'ledger' ? ' adm-btn--active' : ''}`} onClick={() => setView('ledger')} data-testid="view-ledger">Movimientos de cristales</button>
        </div>
      </div>
      {view === 'compras' ? <PurchasesView onViewStats={onViewStats} /> : <LedgerView onViewStats={onViewStats} />}
    </div>
  );
}
